import React, { useState, useEffect } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, LinearProgress, FormControlLabel, RadioGroup, Radio, Typography, ClickAwayListener, Tooltip } from "@material-ui/core";
import { BookingType, Booking, BookingCancellationCost, getBookingCancellationCost, deleteBooking, getDeclineWillCancel } from "api/bookings";
import { useSnackbar } from "notistack";
import { GridTextField } from "components";
import { Membership } from "api/memberships";
import { Club } from "api/clubs";
import i18n from "../i18n";

// prop types
interface CancelBookingDialogProps {
    club: Club | null,
    booking?: Booking,
    show: boolean,
    userId?: string,
    userRole?: Membership["userRole"],
    onCancel: () => void,
    onClose: () => void,
    onFetchBookings: () => void,
    getCompanyBookings: (clubId: string) => void,
}

// currency formatter
const formatter = Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" });

// cancel booking dialog
const CancelBookingDialog = ({ club, booking, show, userId, userRole, onCancel, onClose, onFetchBookings, getCompanyBookings }: CancelBookingDialogProps) => {
    // snackbar
    const { enqueueSnackbar } = useSnackbar();
    const displayError = (error: any) => enqueueSnackbar(
        `${i18n.anErrorOccurred}: ${error.description} (${error.type})`,
        { variant: "error" },
    );

    // ui state
    const [cancellationCost, setCancellationCost] = useState<BookingCancellationCost>();
    const [cancellationReason, setCancellationReason] = useState("");
    const [declineWillCancel, setDeclineWillCancel] = useState(false);
    const [groupId, setGroupId] = useState("");
    const [isLoading, setLoading] = useState(false);
    const [forceCancel, setForceCancel] = useState("false");
    const [showCancelRepeatedBookingDialog, setShowCancelRepeatedBookingDialog] = useState(false);
    const [showCancelReasonTooltip, setShowCancelReasonTooltip] = useState(false);
    const participant = booking?.participants.find((p) => p.userId === userId);

    // fetch cancellation info
    const fetchCancellationInfo = async (bookingId: string) => {
        setCancellationCost(await getBookingCancellationCost({ bookingId }));
        setDeclineWillCancel((await getDeclineWillCancel({ bookingId })).declineWillCancelBooking);
    };
    useEffect(() => { if (booking) fetchCancellationInfo(booking.id); }, [booking]);

    // reset ui
    useEffect(() => {
        if (!show) {
            setCancellationReason("");
            setForceCancel("false");
            setGroupId("");
        }
    }, [show]);

    // api request
    const saveAction = async () => {
        setShowCancelRepeatedBookingDialog(false);
        setLoading(true);
        try {
            await deleteBooking({
                bookingId: booking!.id,
                cancellationReason: cancellationReason || undefined,
                expectedPriceCents: cancellationCost?.priceCents || 0,
                forceCancel: forceCancel === "true",
                groupId: groupId || undefined,
            });
            onFetchBookings();
            getCompanyBookings(club!.id);
            onClose();
        } catch (error) {
            displayError(error);
            fetchCancellationInfo(booking!.id);
        }
        setLoading(false);
    };

    // render
    const price = (cancellationCost?.priceCents ? formatter.format(cancellationCost.priceCents / 100) : "");
    return (
        <>
            <Dialog
                fullWidth
                open={show}
                onClose={onClose}
            >
                <DialogTitle>{i18n.cancelBooking}</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>

                        {/* blocking */}
                        {booking?.bookingType === BookingType.Blocking && (
                            <Grid item xs={12}>
                                <Typography>
                                    {i18n.leaveBookingBlocking}
                                </Typography>
                            </Grid>
                        )}

                        {/* other -> depends whether admin or participant of the booking */}
                        {booking?.bookingType !== BookingType.Blocking && (
                            <>
                                {/* cancel options -> if coach or admin */}
                                {((participant?.invitationStatus === "accepted" && participant.participantRole === "coach") || ["admin", "owner"].includes(userRole!)) && (
                                    <Grid item xs={12}>
                                        <RadioGroup value={forceCancel} onChange={(e) => setForceCancel(e.target.value)}>
                                            <FormControlLabel value="false" control={<Radio color="primary" />} label={i18n.leaveBooking} />
                                            <FormControlLabel value="true" control={<Radio color="primary" />} label={i18n.cancelBooking} />
                                        </RadioGroup>
                                    </Grid>
                                )}

                                {/* cancellation reason */}
                                {forceCancel === "true" && (
                                    <GridTextField
                                        helperText={i18n.cancellationReasonLength}
                                        label={i18n.cancellationReason}
                                        maxLength={100}
                                        size={12}
                                        value={cancellationReason}
                                        onChange={setCancellationReason}
                                    />
                                )}

                                {/* desc */}
                                <Grid item xs={12}>
                                    <Typography>
                                        {(declineWillCancel || forceCancel === "true") && i18n.leaveBookingDeclineWillCancel}
                                        {participant && forceCancel === "false" && i18n.leaveBookingPrice(price)}
                                    </Typography>
                                </Grid>
                            </>
                        )}

                    </Grid>
                </DialogContent>
                {isLoading && <LinearProgress />}
                <DialogActions>
                    <Button color="secondary" onClick={onCancel}>
                        {i18n.cancel}
                    </Button>
                    {(forceCancel === "true" && cancellationReason.length < 4) && booking!.bookingType !== BookingType.Blocking ? (
                        <ClickAwayListener onClickAway={() => setShowCancelReasonTooltip(false)}>
                            <div>
                                <Tooltip
                                    PopperProps={{
                                        disablePortal: true,
                                    }}
                                    onClose={() => setShowCancelReasonTooltip(false)}
                                    open={showCancelReasonTooltip}
                                    placement="bottom-end"
                                    disableFocusListener
                                    disableHoverListener
                                    disableTouchListener
                                    title={i18n.cancelReasonMessage}
                                    arrow
                                >
                                    <Button color="primary" disabled={isLoading} onClick={() => setShowCancelReasonTooltip(true)}>
                                        {i18n.cancelB}
                                    </Button>
                                </Tooltip>
                            </div>
                        </ClickAwayListener>
                    ) : (
                        <Button color="primary" disabled={isLoading} onClick={(booking?.repeatType !== "never") ? () => setShowCancelRepeatedBookingDialog(true) : saveAction}>
                            {i18n.cancelB}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* cancel repeated booking dialog */}
            <Dialog
                open={showCancelRepeatedBookingDialog}
                onClose={() => setShowCancelRepeatedBookingDialog(false)}
            >
                <DialogTitle>{i18n.cancelRepeatedBooking}</DialogTitle>
                <DialogContent dividers>
                    <RadioGroup value={groupId} onChange={(e) => setGroupId(e.target.value)}>
                        <FormControlLabel value="" control={<Radio color="primary" />} label={i18n.thisBooking} />
                        <FormControlLabel value={booking?.groupId} control={<Radio color="primary" />} label={i18n.thisBookingAndFollowing} />
                    </RadioGroup>
                </DialogContent>
                <DialogActions>
                    <Button color="secondary" onClick={() => setShowCancelRepeatedBookingDialog(false)}>
                        {i18n.cancel}
                    </Button>
                    <Button color="primary" onClick={saveAction}>
                        {i18n.ok}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default CancelBookingDialog;
